import { useState, useEffect, useRef } from "react";
import { Menu, X } from "lucide-react";
import { IMAGES } from "@/assets/images";
import type { NavItem } from "@/lib/index";

interface LayoutProps {
  children: React.ReactNode;
}

const navItems: NavItem[] = [
  { label: "Inicio", href: "#hero" },
  { label: "Nosotros", href: "#nosotros" },
  { label: "Servicios", href: "#servicios" },
  { label: "Contacto", href: "#contacto" }
];

export function Layout({ children }: LayoutProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const updateHeight = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        document.documentElement.style.setProperty('--header-height', `${height}px`);
      }
    };

    updateHeight();
    const resizeObserver = new ResizeObserver(updateHeight);
    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }

    return () => resizeObserver.disconnect();
  }, []);

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <header
        ref={headerRef}
        className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border transition-all duration-200"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-20">
            <a href="#hero" className="flex-shrink-0">
              <img
                src={IMAGES.WHATSAPP_IMAGE_2026_05_06_AT_08_37_00_27}
                alt="Soluciones Innovadoras S.I. S.A.S."
                className="h-12 w-auto"
              />
            </a>

            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  className="text-foreground/80 hover:text-accent font-medium transition-colors duration-200 relative group"
                >
                  {item.label}
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-accent transition-all duration-200 group-hover:w-full" />
                </a>
              ))}
            </nav>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 text-foreground hover:text-accent transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden border-t border-border bg-background">
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-4">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={handleNavClick}
                  className="text-foreground/80 hover:text-accent font-medium transition-colors duration-200 py-2"
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </header>

      <main style={{ paddingTop: `${headerHeight}px` }} className="flex-1">
        {children}
      </main>

      <footer className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img
                src={IMAGES.WHATSAPP_IMAGE_2026_05_06_AT_08_37_00_28}
                alt="Soluciones Innovadoras S.I. S.A.S."
                className="h-12 w-auto mb-4 brightness-0 invert"
              />
              <p className="text-primary-foreground/80 text-sm leading-relaxed">
                Transformación digital para el sector público colombiano
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Enlaces Rápidos</h3>
              <nav className="flex flex-col gap-2">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="text-primary-foreground/80 hover:text-accent transition-colors duration-200 text-sm"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>

            <div>
              <h3 className="font-semibold text-lg mb-4">Contacto</h3>
              <div className="text-primary-foreground/80 text-sm space-y-2">
                <p>Colombia</p>
                <p>NIT: 901.873.069-1</p>
                <p className="text-xs mt-4 text-primary-foreground/60">
                  Información de contacto disponible próximamente
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-primary-foreground/20 mt-8 pt-8 text-center">
            <p className="text-primary-foreground/60 text-sm">
              © 2026 Soluciones Innovadoras S.I. S.A.S. Todos los derechos reservados.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}